import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import { formatDateOnly, formatDateWithoutTime } from '../../../../app/helper-functions/DateFunction';
import { get_leave_reports } from '../../../../app/pages/Reports/leave/api/leaveReportApi';
import { useQuery } from 'react-query';
import { get_all_users } from '../../../../app/pages/UserManagement/users/api/usersApi';
import { User } from '../../../../app/modules/accounts/components/settings/SettingsModel';
import { KTIcon } from '../../../helpers';
// Define an interface matching the API response data item structure from your JSON
interface LeaveRecord {
    id: number;
    leave_policy_id: number;
    leave_policy_name: string; // Matches JSON
    user_id: number;
    user_name: string; // Matches JSON
    employee_email: string; // Matches JSON
    from_date: string; // Matches JSON (YYYY-MM-DD)
    to_date: string; // Matches JSON (YYYY-MM-DD)
    leave_reason: string | null; // Matches JSON
    status: string; // Matches JSON (Approved, Pending, Rejected)
    approval_message: string | null; // Matches JSON
    reason_for_cancel: string | null; // Matches JSON
    is_cancelled: number; // Matches JSON (0 or 1)
    approved_by: number | null; // Matches JSON (user ID)
    team_email: string | null; // Matches JSON
    created_at: string;
    updated_at: string;
    updated_by: number | null; // Matches JSON (user ID)
    updated_by_name: string | null; // Matches JSON
}
function EmployeesLeaveTodayWidget() {
      // --- State Declarations ---
        const [searchInput, setSearchInput] = useState<string>('');
        const [currentPage, setCurrentPage] = useState<number>(1);
        // Use the new interface for state data
        const [visibleData, setVisibleData] = useState<LeaveRecord[]>([]);
        const [fullReportData, setFullReportData] = useState<LeaveRecord[]>([]);
        const [selectedItemsPerPage, setSelectedItemsPerPage] = useState<number>(10);
        // Update sortBy type to use the new interface keys
        const [sortBy, setSortBy] = useState<{ key: keyof LeaveRecord | string, ascending: boolean } | null>(null);
    const [filterPayload, setFilterPayload] = useState<any>({
        from_date: formatDateOnly(new Date()),
        to_date: formatDateOnly(new Date()),
        date_flag: "specific"
    });
    const [userList, setUserList] = useState<User[]>([]);
        const { data: userData, isLoading: isLoadingUsers } = useQuery(
            'get_all_users',
            get_all_users,
            { refetchOnWindowFocus: false }
        );
    
        // Populate user list from fetched data
        useEffect(() => {
            if (Array.isArray(userData?.data)) {
                setUserList(userData.data as User[]);
            } else {
                setUserList([]);
            }
        }, [userData]);
    
        // Fetch leave Report Data
        const {
            data: filterApiResponse,
            isLoading: isReportLoading,
            isError: isReportError,
            isFetching,
        } = useQuery(
            ['leaveReportData', filterPayload], // Query key includes payload
            () => get_leave_reports(filterPayload), // API function call
            {
                onSuccess: (apiResponse) => {
                    // Ensure data structure matches interface
                    if (apiResponse?.status && Array.isArray(apiResponse.data)) {
                        // Type assertion updated to the new interface
                        setFullReportData(apiResponse.data as LeaveRecord[]);
                    } else {
                        setFullReportData([]);
                        // Show error toast only if filters were applied and failed OR if API status is false on default load
                        if (apiResponse && !apiResponse.status) {
                             const message = apiResponse.message || (Object.keys(filterPayload).length > 0 ? "Failed to fetch filtered report." : "Failed to fetch leave report.");
                             if (Object.keys(filterPayload).length > 0 || fullReportData.length > 0) { // Only show toast if filters applied or there was previous data
                                toast.error(message, { position: 'top-right', autoClose: 4000 });
                             } else {
                                 console.warn("API status false on initial load:", message); // Log silently on initial empty load
                             }
                        }
                    }
                },
                onError: (error: any) => {
                    const errorMessage = error?.response?.data?.message || "Failed to fetch leave report.";
                    toast.error(errorMessage, { position: 'top-right', autoClose: 4000 });
                    setFullReportData([]); // Clear report data on error
                },
                enabled: true, // Fetch data on mount and when filterPayload changes
                refetchOnWindowFocus: false,
                keepPreviousData: true,
            }
        );
    
        // --- Derived State & Pagination/Sorting Logic (Client-side) ---
        const itemsPerPageOptions = [10, 20, 30, 50, 100];
        const totalItems = fullReportData.length; // Total items received from API based on server filters
        const totalPages = Math.ceil(totalItems / selectedItemsPerPage);
    
        // Effect to apply client-side search, sorting, and pagination
        useEffect(() => {
            let dataToProcess = [...fullReportData]; // Start with data from API after server filters
    
            // Apply Search Filter (Client-side)
            if (searchInput) {
                const searchTerm = searchInput.toLowerCase();
                dataToProcess = dataToProcess.filter((item) =>
                     // Search fields relevant to the LeaveRecord interface
                    item.user_name?.toLowerCase().includes(searchTerm) ||
                    item.leave_policy_name?.toLowerCase().includes(searchTerm) ||
                    item.leave_reason?.toLowerCase().includes(searchTerm) ||
                     item.status?.toLowerCase().includes(searchTerm) // Also allow searching status text
                );
            }
    
            // Apply Sorting (Client-side)
            if (sortBy) {
                dataToProcess.sort((a, b) => {
                    // Use the new interface keys for sorting
                     const key = sortBy.key as keyof LeaveRecord;
                    const aValue = a[key] ?? '';
                    const bValue = b[key] ?? '';
    
                    // Basic locale-sensitive comparison for strings, direct for others
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        return sortBy.ascending ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                    } else {
                        // Basic comparison for numbers/dates (if they were Date objects, need different logic)
                        // For YYYY-MM-DD strings, localeCompare works for date sorting
                        if (aValue < bValue) return sortBy.ascending ? -1 : 1;
                        if (aValue > bValue) return sortBy.ascending ? 1 : -1;
                        return 0;
                    }
                });
            }
    
            // Apply Pagination (Client-side)
            const startIndex = (currentPage - 1) * selectedItemsPerPage;
            const endIndex = startIndex + selectedItemsPerPage;
            setVisibleData(dataToProcess.slice(startIndex, endIndex));
    
            // Reset to page 1 if current page becomes invalid after client-side search filtering
             // This should happen when the dataToProcess length changes due to search
            if (currentPage > 1 && startIndex >= dataToProcess.length && dataToProcess.length > 0) {
                 setCurrentPage(1);
            } else if (dataToProcess.length === 0) {
                 setCurrentPage(1); // Always reset to 1 if search results in no data
            }
    
    
        }, [fullReportData, currentPage, selectedItemsPerPage, sortBy, searchInput]); // Recalculate when these change
    
        // Client-side Search Handler
        const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
            setSearchInput(e.currentTarget.value);
            // Note: setCurrentPage(1) is handled in the useEffect based on dataToProcess length change
        }, []);
    
        // Client-side Sort Handler
        // Updated key type
        const handleSort = (key: keyof LeaveRecord | string) => {
            if (!sortBy || sortBy.key !== key) {
                setSortBy({ key, ascending: true });
            } else {
                setSortBy({ key, ascending: !sortBy.ascending });
            }
            setCurrentPage(1); // Reset to first page when sorting
        };
    
        // Pagination Handlers (Client-side)
        const handleNextPage = () => {
            if (currentPage < totalPages) {
                setCurrentPage(currentPage + 1);
            }
        };
        const handlePreviousPage = () => {
            if (currentPage > 1) {
                setCurrentPage(currentPage - 1);
            }
        };
        const handleItemsPerPageChange = (e: ChangeEvent<HTMLSelectElement>) => {
            setSelectedItemsPerPage(Number(e.target.value));
            setCurrentPage(1); // Reset to first page
        };
    
        // Loading state for filter dropdowns
        const isInitialLoadingFilters =  isLoadingUsers;
        // Overall loading state: True if filter options are loading OR the main report query is loading/fetching
        const isLoading = isInitialLoadingFilters || isReportLoading || isFetching;
  return (
    <div className="card-stretch mb-5 shadow-sm">
      <div className='card mb-5 mb-xl-10'>
                 {/* Listing Section */}
                <div className='card-header border-0 pt-5'>
                    <h3 className='card-title align-items-start flex-column'>
                        <span className='card-label fw-bolder fs-3 mb-1'>Employees on Leave Today</span>
                    </h3>
                     {/* Search Input */}
                    <div className='card-title'>
                        <div className='d-flex align-items-center position-relative my-1'>
                            <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                            <input
                                type='text'
                                data-kt-user-table-filter='search'
                                className='form-control form-control-solid w-250px ps-14'
                                placeholder='Search records...'
                                value={searchInput}
                                onChange={handleInputChange}
                                // Disable search if loading report or no data available to search
                                disabled={isReportLoading || isFetching || fullReportData.length === 0}
                            />
                        </div>
                    </div>
                </div>

                <div className='card-body py-3'>
                    <div className='table-responsive'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                            <thead>
                                <tr className='fw-bold text-muted bg-light'>
                                    {/* Sorting Headers - Updated keys */}
                                    <th className='min-w-200px cursor-pointer' onClick={() => handleSort('user_name')}> {/* Removed ps-4 as user has padding */}
                                        Employee {sortBy?.key === 'user_name' && <KTIcon iconName={sortBy.ascending ? 'arrow-up' : 'arrow-down'} className='fs-5 ms-1' />}
                                    </th>
                                    <th className='min-w-200px ps-4 rounded-start cursor-pointer' onClick={() => handleSort('leave_policy_name')}>
                                        Leave Policy {sortBy?.key === 'leave_policy_name' && <KTIcon iconName={sortBy.ascending ? 'arrow-up' : 'arrow-down'} className='fs-5 ms-1' />}
                                    </th>
                                    <th className='min-w-120px text-center cursor-pointer' onClick={() => handleSort('from_date')}> {/* Centered date headers */}
                                       From Date {sortBy?.key === 'from_date' && <KTIcon iconName={sortBy.ascending ? 'arrow-up' : 'arrow-down'} className='fs-5 ms-1' />}
                                    </th>
                                    <th className='min-w-120px text-center cursor-pointer' onClick={() => handleSort('to_date')}> {/* Corrected key and centered */}
                                       To Date {sortBy?.key === 'to_date' && <KTIcon iconName={sortBy.ascending ? 'arrow-up' : 'arrow-down'} className='fs-5 ms-1' />}
                                    </th>
                                    <th className='min-w-120px text-center cursor-pointer' onClick={() => handleSort('status')}> {/* Centered */}
                                        Status {sortBy?.key === 'status' && <KTIcon iconName={sortBy.ascending ? 'arrow-up' : 'arrow-down'} className='fs-5 ms-1' />}
                                    </th>
                                    <th className='min-w-120px text-center cursor-pointer' onClick={() => handleSort('approved_by')}> {/* Centered */}
                                    Approved By{sortBy?.key === 'approved_by' && <KTIcon iconName={sortBy.ascending ? 'arrow-up' : 'arrow-down'} className='fs-5 ms-1' />}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Loading State */}
                                {isLoading && ( // Use overall loading state
                                    <tr>
                                        <td colSpan={7} className="text-center py-10"> {/* Adjusted colspan */}
                                            <div className='spinner-border text-primary' role='status'>
                                                <span className='visually-hidden'>Loading...</span>
                                            </div>
                                            <span className="ms-3 text-muted">Loading leave data...</span>
                                        </td>
                                    </tr>
                                )}

                                {/* Error State */}
                                {!isLoading && isReportError && ( // Show error if not loading and there was an error
                                    <tr>
                                        <td colSpan={7} className="text-center py-10"> {/* Adjusted colspan */}
                                            <div className="alert alert-danger d-inline-flex align-items-center p-3">
                                                <i className="bi bi-exclamation-triangle-fill fs-2 me-2"></i>
                                                Error loading leave report. Please try again later or contact support.
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {/* No Data State */}
                                {/* Show no data if not loading, no error, AND visible data is empty */}
                                {!isLoading && !isReportError && visibleData.length === 0 && (
                                     <tr>
                                        <td colSpan={7} className="text-center py-10 text-muted"> {/* Adjusted colspan */}
                                             <i className="bi bi-cloud-slash fs-2hx"></i>
                                             <p className="mt-3">
                                                {searchInput
                                                    ? 'No records found matching your search.'
                                                    : (Object.keys(filterPayload).length > 0
                                                        ? 'No records found matching your filter criteria.'
                                                        : 'No leave records available.') // Default if API returns empty initially or no filters yield results
                                                }
                                             </p>
                                        </td>
                                    </tr>
                                )}

                                {/* Display Data - Using LeaveRecord interface */}
                                {!isLoading && !isReportError && visibleData.length > 0 && (
                                    visibleData.map((record) => ( // Renamed variable for clarity
                                        <tr key={record.id}>
                                               <td> {/* Employee cell */}
                                                <div className='d-flex align-items-center'>
                                                    <div className="symbol symbol-40px me-3">
                                                        <span className="symbol-label bg-light-primary text-primary fw-semibold">
                                                            {record.user_name?.charAt(0).toUpperCase() || '?'}
                                                        </span>
                                                    </div>
                                                    <div className='d-flex justify-content-start flex-column'>
                                                        <span className='text-dark fw-bold text-hover-primary mb-1 fs-6'>{record.user_name || 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </td>
                                               <td className="ps-4"> 
                                                <span className="text-dark fw-semibold d-block fs-7">{record.leave_policy_name || 'N/A'}</span>
                                            </td>
                                            <td className="text-center"> {/* Centered */}
                                                <span className="text-dark fw-semibold d-block fs-7">{formatDateWithoutTime(record.from_date) || 'N/A'}</span>
                                            </td>
                                            <td className="text-center"> {/* Centered */}
                                                <span className="text-dark fw-semibold d-block fs-7">{formatDateWithoutTime(record.to_date) || 'N/A'}</span> {/* Corrected key */}
                                            </td>
                                            <td className="text-center"> {/* Centered */}
                                                <span className="text-dark fw-semibold d-block fs-7 text-truncate">
                                                    {record.status || '-'}
                                                </span>
                                            </td>
                                            {/* Approved By cell */}
                                            <td className="text-center rounded-end"> {/* Centered */}
                                                 <span className="text-dark fw-semibold d-block fs-7">
                                                     {/* Display approver name if status is Approved, fallback to ID if name null, otherwise '-' */}
                                                     {record.status === 'Approved' ? (record.approved_by || (record.approved_by ? `User ID: ${record.approved_by}` : '-')) : '-'}
                                                 </span>
                                             </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer with Pagination */}
                <div className='card-footer d-flex justify-content-between align-items-center py-4'>
                    {/* Items Per Page Dropdown */}
                    <div className='d-flex align-items-center'>
                        <span className='fw-semibold me-2'>Results Per Page:</span>
                        <select
                            className='form-select form-select-sm form-select-solid w-auto'
                            value={selectedItemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={totalItems === 0 || isLoading} // Disable if no data or loading
                        >
                            {itemsPerPageOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>

                    {/* Pagination Controls */}
                     <div className='d-flex'>
                        {/* Show pagination only if there's data and more than one page */}
                        { totalPages > 1 && !isLoading && (
                            <>
                                <button
                                    className={`btn btn-sm ${currentPage === 1 ? 'btn-light disabled' : 'btn-primary'} me-2`}
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                <span className='btn btn-sm btn-light disabled mx-2'>
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    className={`btn btn-sm ${currentPage === totalPages ? 'btn-light disabled' : 'btn-primary'}`}
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </>
                         )}
                         {/* Show placeholder or message if only one page or loading */}
                         { (totalPages <= 1 || isLoading) && !isReportError && totalItems > 0 && ( // Show if one page, loading, AND there are items
                             <span className='btn btn-sm btn-light disabled'>
                                 Page 1 of 1
                            </span>
                         )}
                          { (totalPages <= 1 || isLoading) && !isReportError && totalItems === 0 && !searchInput && ( // Show if no items and no search
                              <span className='btn btn-sm btn-light disabled'>
                                 No Data
                            </span>
                         )}
                    </div>
                </div>
            </div>
    </div>
  )
}

export default EmployeesLeaveTodayWidget
